import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useToast } from "@/components/ui/use-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  CardFooter,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2,
  CreditCard,
  Calendar,
  Lock,
  User,
  ShieldCheck,
  ArrowLeft,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { useTheme } from "../components/ThemeProvider";
import {
  createSubscription,
  updateSubscription,
} from "../store/membership/membershipActions";
import { setIsPlanProcessing } from "../store/membership/membershipSlice";
import {
  getUserDefaultPaymentMethod,
  getUserSubscription,
  refreshUser,
} from "../store/auth/authActions";

const PaymentCard = ({ plan, isUpgrade }) => {
  const { toast } = useToast();
  const { resolvedTheme } = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const stripe = useStripe();
  const elements = useElements();
  const { user, currentSubscription } = useSelector((state) => state.auth);
  const { isSubscriptionLoading, isPlanProcessing } = useSelector(
    (state) => state.membership,
  );
  const [cardName, setCardName] = useState("");

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (isPlanProcessing) return;

    if (!stripe || !elements) {
      toast({
        title: "Stripe not loaded",
        description: "Please try again later.",
        variant: "destructive",
      });
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    if (!cardName) {
      toast({
        title: "Card Holder Name",
        description: "Please enter your card holder name.",
        variant: "destructive",
      });
      return;
    }

    if (!cardElement) {
      toast({
        title: "Card Error",
        description: "Card details are missing.",
        variant: "destructive",
      });
      return;
    }

    dispatch(setIsPlanProcessing(true));

    try {
      const { error: pmError, paymentMethod } =
        await stripe.createPaymentMethod({
          type: "card",
          card: cardElement,
          billing_details: {
            name: cardName,
            email: user.email,
          },
        });

      if (pmError) {
        console.error("Stripe Error:", pmError.message);
        toast({
          title: "Stripe Error",
          description:
            pmError.message ||
            "Failed to generate payment method. Please check your card details.",
          variant: "destructive",
        });
        return;
      }

      toast({
        title: isUpgrade ? "Processing Upgrade..." : "Processing Payment...",
        description: isUpgrade
          ? "Please wait while we upgrade your subscription."
          : "Please wait while we complete your subscription.",
      });

      if (isUpgrade || currentSubscription?.status === "active") {
        const payload = {
          subscriptionId: currentSubscription.stripeSubscriptionId,
          newPriceId: plan.stripePriceId,
          paymentMethodId: paymentMethod.id,
        };

        await dispatch(updateSubscription({ payload })).unwrap();

        toast({
          title: "Upgrade Successful!",
          description: `Your account has been upgraded to the ${plan.name} plan. Redirecting to your dashboard...`,
          variant: "success",
          duration: 3000,
        });
      } else {
        const payload = {
          userId: user.uid,
          planId: plan.id,
          paymentMethodId: paymentMethod.id,
        };

        await dispatch(createSubscription({ payload })).unwrap();

        toast({
          title: "Payment Successful!",
          description: `Your ${plan.name} plan is now active. Redirecting to your dashboard...`,
          variant: "success",
          duration: 3000,
        });
      }

      await dispatch(refreshUser()).unwrap();
      await dispatch(getUserSubscription(user?.uid)).unwrap();
      await dispatch(getUserDefaultPaymentMethod(user?.uid)).unwrap();

      navigate("/dashboard", {
        state: { plan: plan.id, planSelected: true },
      });
    } catch (err) {
      console.error("Payment failed:", err);
    } finally {
      dispatch(setIsPlanProcessing(false));
    }
  };

  const stripeElementOptions = {
    style: {
      base: {
        fontSize: "16px",
        color: resolvedTheme === "dark" ? "#f9fafb" : "#0f172a",
        "::placeholder": {
          color: resolvedTheme === "dark" ? "#9ca3af" : "#6b7280",
        },
      },
      invalid: {
        color: "#ef4444",
      },
    },
  };

  return (
    <Card className="w-full max-w-xl shadow-2xl rounded-2xl border border-primary/30 bg-gradient-to-br from-background/90 via-card/80 to-secondary/30 backdrop-blur-md">
      <CardHeader className="text-center space-y-2 pb-6">
        <CardTitle className="text-[22px] xs:text-2xl md:text-3xl font-bold">
          {isUpgrade ? "Complete Your Upgrade" : "Complete Your Purchase"}
        </CardTitle>
        <CardDescription className="text-sm md:text-base text-muted-foreground pt-1">
          {isUpgrade
            ? "You are upgrading to the "
            : "You are subscribing to the "}
          <span className="font-semibold text-primary">{plan.name}</span> plan.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* Summary Section */}
        <div className="border rounded-xl p-5 bg-secondary/20 shadow-inner relative">
          <div className="flex justify-between flex-wrap items-center">
            <h3 className="font-semibold mb-3 text-base md:text-lg">
              {isUpgrade ? "Upgrade Summary:" : "Payment Summary:"}
            </h3>
          </div>

          <div className="flex justify-between items-center mt-2">
            <span className="text-muted-foreground">
              {isUpgrade ? "Upgrade Amount:" : "Total Due Today:"}
            </span>
            <span className="text-xl md:text-3xl font-bold text-primary flex items-center gap-2">
              ${plan.price}{" "}
              <span className="text-xs xs:text-sm font-normal text-muted-foreground">
                /{plan.frequency}
              </span>
            </span>
          </div>
        </div>

        {/* Card Input Section */}
        <form onSubmit={handlePaymentSubmit} className="space-y-6">
          <p className="text-sm text-center text-muted-foreground font-medium">
            {isUpgrade
              ? "Enter your card details to complete your upgrade"
              : "Enter your card details to complete your purchase"}
          </p>

          {/* Name on Card */}
          <div className="space-y-2">
            <Label htmlFor="cardName">Name on Card</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                id="cardName"
                type="text"
                placeholder="John Doe"
                required
                className="pl-12 py-3 text-base rounded-lg"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
              />
            </div>
          </div>

          {/* Card Number */}
          <div className="space-y-2">
            <Label htmlFor="cardNumber">Card Number</Label>
            <div className="relative">
              <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <div className="pl-12 border rounded-lg h-12 flex items-center px-3 bg-background shadow-sm">
                <CardNumberElement
                  id="cardNumber"
                  options={stripeElementOptions}
                  className="w-full text-base"
                />
              </div>
            </div>
          </div>

          {/* Expiry & CVC */}
          <div className="grid grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="expiryDate">Expiry Date</Label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <div className="pl-12 border rounded-lg h-12 flex items-center px-3 bg-background shadow-sm">
                  <CardExpiryElement
                    id="expiryDate"
                    options={stripeElementOptions}
                    className="w-full text-base"
                  />
                </div>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cvc">CVC</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <div className="pl-12 border rounded-lg h-12 flex items-center px-3 bg-background shadow-sm">
                  <CardCvcElement
                    id="cvc"
                    options={stripeElementOptions}
                    className="w-full text-base"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* CTA Button */}
          <Button
            type="submit"
            size="lg"
            className="w-full bg-gradient-to-r from-primary to-accent text-primary-foreground hover:opacity-90 transition-all duration-200 text-base md:text-lg py-3 md:py-5 rounded-xl shadow-md"
            disabled={
              isPlanProcessing || isSubscriptionLoading || !stripe || !elements
            }
          >
            {isPlanProcessing || isSubscriptionLoading ? (
              <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            ) : (
              <ShieldCheck className="mr-2 h-5 w-5 flex-shrink-0" />
            )}
            {isPlanProcessing || isSubscriptionLoading
              ? "Processing..."
              : isUpgrade
                ? `Confirm $${plan?.price} Upgrade`
                : `Pay $${plan?.price} and Activate Plan`}
          </Button>
        </form>
      </CardContent>

      <CardFooter className="text-xs text-muted-foreground text-center block space-y-2 pt-4">
        <p>
          By continuing, you agree to our{" "}
          <Link to="/terms" className="underline font-medium">
            Terms of Service
          </Link>
          . Your subscription will auto-renew.
        </p>
        <Link
          to="/list-inventory"
          className="text-primary hover:underline block font-semibold"
        >
          Change Plan
        </Link>
      </CardFooter>
    </Card>
  );
};

const PaymentPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const {
    isAuthenticated,
    user,
    currentSubscription,
    isLoading: isAuthLoading,
  } = useSelector((state) => state.auth);

  const plan = location.state?.plan;
  const isUpgrade = location.state?.isUpgrade || false;

  useEffect(() => {
    if (isAuthLoading) return;

    if (!isAuthenticated || !user || user?.role !== "seller") {
      toast({
        title: "Access Denied",
        description: "You must be logged in as a seller to make a payment.",
        variant: "destructive",
      });
      navigate("/list-inventory");
    }

    if (!plan) {
      toast({
        title: "No Plan Selected",
        description: "Please select a membership plan first.",
        variant: "destructive",
      });
      navigate("/list-inventory");
    }

    if (
      isUpgrade &&
      (!currentSubscription || !currentSubscription.stripeSubscriptionId)
    ) {
      toast({
        title: "Upgrade Error",
        description: "You don't have an active subscription to upgrade.",
        variant: "destructive",
      });
      navigate("/list-inventory");
    }
  }, [isAuthenticated, user, plan, navigate, toast, isAuthLoading, isUpgrade]);

  if (isAuthLoading || !plan) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <Loader2 className="h-12 w-12 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      className="flex flex-col gap-3 justify-center items-center py-12"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PaymentCard plan={plan} isUpgrade={isUpgrade} />
    </motion.div>
  );
};

export default PaymentPage;
